import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getCollectionReportCalendar, getCollectionReportModel, setCollectionReportClosures, setCollectionReportModel } from "@/lib/api";
import { apiError } from "./reportFormat";

// Admin-only settings for the current region's report: model target inputs + team closure days (non-working, greyed in the grid).
export function ReportSettingsDialog({ open, onClose, aus, year, onSaved }) {
  const [rate, setRate] = useState("23");
  const [days, setDays] = useState("21.1");
  const [closures, setClosures] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [m, cal] = await Promise.all([getCollectionReportModel(), getCollectionReportCalendar()]);
        setRate(String(Math.round((m.rate || 0) * 10000) / 100));
        setDays(String(m.avg_working_days ?? ""));
        setClosures((cal.closures || []).join("\n"));
        setHolidays((cal.bank_holidays || []).filter((d) => d.startsWith(String(year))).map((d) => ({ date: d, name: cal.holiday_names?.[d] })));
      } catch (e) {
        toast.error(apiError(e, "Could not load report settings"));
      }
    })();
  }, [open, year]);

  const save = async () => {
    setSaving(true);
    try {
      const dates = closures.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      if (dates.some((d) => !/^\d{4}-\d{2}-\d{2}$/.test(d))) throw new Error("Closure dates must be YYYY-MM-DD, one per line");
      await setCollectionReportModel(Number(rate) / 100, Number(days));
      await setCollectionReportClosures(dates);
      toast.success("Report settings saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="report-settings-dialog">
        <DialogHeader>
          <DialogTitle>{aus ? "AU" : "UK"} Collection Report settings</DialogTitle>
          <DialogDescription>Model target = receivables on day 1 × rate ÷ average working days × this month's working days. Closures are team days off — rows grey out and cash rolls into the previous working day.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="space-y-1"><span className="text-xs text-slate-500">Model rate (% of receivables)</span>
            <Input value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" data-testid="report-model-rate" /></label>
          <label className="space-y-1"><span className="text-xs text-slate-500">Avg working days / month</span>
            <Input value={days} onChange={(e) => setDays(e.target.value)} inputMode="decimal" data-testid="report-model-days" /></label>
        </div>
        <label className="space-y-1 text-sm"><span className="text-xs text-slate-500">Team closures (YYYY-MM-DD, one per line)</span>
          <Textarea rows={4} value={closures} onChange={(e) => setClosures(e.target.value)} placeholder="2026-12-24" data-testid="report-closures" /></label>
        <div className="text-xs text-slate-500" data-testid="report-holidays">
          <span className="font-medium">{aus ? "NSW public holidays" : "Bank holidays"} {year}:</span>{" "}
          {holidays.length ? holidays.map((h) => `${h.date.slice(5)}${h.name && aus ? ` ${h.name}` : ""}`).join(" · ") : "—"}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="report-settings-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="report-settings-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
